import { ChangeDetectorRef, Component, DestroyRef, Input, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SocialFeedService } from '../../services/social-feed.service';
import { SocialHubService, SocialHubState } from '../../services/social-hub.service';
import { SocialNotification } from '../../models/social-feed.model';

export type SocialSection = 'feed' | 'communities' | 'notifications' | 'messages' | 'profile' | 'none';

/**
 * The frame every social page sits in: your profile and shortcuts on the
 * left, the page in the middle, notifications / invites / communities to
 * discover on the right. On narrower screens the right column goes, and on
 * phones the left one becomes a row of shortcuts above the page.
 */
@Component({
  selector: 'app-social-shell',
  templateUrl: './social-shell.component.html',
  standalone: false,
})
export class SocialShellComponent implements OnInit, OnDestroy {
  /** Which shortcut is highlighted. */
  @Input() active: SocialSection = 'none';

  hub!: SocialHubState;
  myId: number | null = null;
  private detach?: () => void;

  constructor(
    private hubService: SocialHubService,
    private social: SocialFeedService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef
  ) {
    this.hub = hubService.snapshot;
  }

  ngOnInit(): void {
    this.hubService.state.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.hub = state;
      this.myId = state.profile?.author.id ?? null;
      this.cdr.markForCheck();
    });
    this.detach = this.hubService.attach();
  }

  ngOnDestroy(): void {
    this.detach?.();
  }

  get recentNotifications(): SocialNotification[] {
    return this.hub.notifications.slice(0, 5);
  }

  openNotification(n: SocialNotification): void {
    this.hubService.markNotificationRead(n);
    const target = SocialHubService.notificationTarget(n);
    if (target) this.router.navigate(target.commands, { queryParams: target.queryParams });
  }

  markAllRead(): void {
    this.hubService.markAllNotificationsRead();
  }

  initials(name: string | undefined | null): string {
    return (name || '')
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }

  timeAgo(isoDate: string): string {
    const minutes = Math.floor((Date.now() - new Date(isoDate).getTime()) / 60000);
    if (minutes < 1) return 'now';
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  }
}
